/**
 * Step Expansion Service
 * 
 * Handles lazy loading of comprehensive step content when users view steps.
 * Provides retry functionality and error handling.
 */

import { populateStepComprehensive, populateStepsBatch } from './assessmentService';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * Generate a summary of previously expanded steps for context
 * @param {Object} planStructure - The full plan structure
 * @param {string} currentStepId - The step being expanded
 * @returns {string} Summary of previous steps
 */
export const generatePreviousStepsSummary = (planStructure, currentStepId) => {
  if (!planStructure) return '';
  
  const expandedSteps = [];
  
  for (const [stepId, step] of Object.entries(planStructure)) {
    // Skip current step, start node, and non-expanded steps
    if (stepId === currentStepId || stepId === 'start' || !step.comprehensiveLoaded) {
      continue;
    }
    
    // Add brief summary of expanded step
    expandedSteps.push(`- ${step.label}: ${step.details?.substring(0, 100)}...`);
  }
  
  if (expandedSteps.length === 0) {
    return 'This is the first step being expanded.';
  }
  
  return `Previously expanded steps (avoid duplicating this content):\n${expandedSteps.join('\n')}`;
};

/**
 * Check if a step needs expansion (skeleton step)
 * @param {Object} step - The step object
 * @returns {boolean} True if step needs comprehensive content loaded
 */
export const needsExpansion = (step) => {
  if (!step) return false;
  
  // Explicitly check the flag
  if (step.comprehensiveLoaded === false) {
    return true;
  }
  
  // Legacy check: if flag doesn't exist, check for presence of comprehensive fields
  if (step.comprehensiveLoaded === undefined) {
    const hasComprehensiveContent = (
      (step.instructions && step.instructions.length > 0) ||
      (step.sops && step.sops.length > 0) ||
      (step.todos && step.todos.length > 0) ||
      (step.tips && step.tips.length > 0)
    );
    return !hasComprehensiveContent;
  }
  
  return false;
};

/**
 * Expand a step with comprehensive content
 * @param {string} planId - The plan ID
 * @param {string} stepId - The step ID to expand
 * @param {Object} stepData - The current step data
 * @param {Object} context - Context containing goal, formData, subcategory, planStructure
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise<{success: boolean, data?: Object, error?: Error, canRetry: boolean}>}
 */
export const expandStep = async (planId, stepId, stepData, context, retryCount = 0) => {
  try {
    console.log(`Expanding step ${stepId} (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
    
    // Generate summary of previous steps for context
    const previousStepsSummary = generatePreviousStepsSummary(context.planStructure, stepId);
    
    const result = await populateStepComprehensive({
      planId,
      stepId,
      stepData,
      goal: context.goal,
      formData: context.formData,
      subcategory: context.subcategory,
      previousStepsSummary, // NEW: Pass context to avoid repetition
    });
    
    console.log(`Step ${stepId} expanded successfully`);
    
    return {
      success: true,
      data: result,
      canRetry: false,
    };
  } catch (error) {
    console.error(`Error expanding step ${stepId} (attempt ${retryCount + 1}):`, error);
    
    // Check if we can retry
    if (retryCount < MAX_RETRIES) {
      console.log(`Retrying step ${stepId} in ${RETRY_DELAY_MS}ms...`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      
      // Recursive retry
      return expandStep(planId, stepId, stepData, context, retryCount + 1);
    }
    
    // All retries exhausted
    return {
      success: false,
      error,
      canRetry: true, // User can manually retry
    };
  }
};

/**
 * Expand multiple steps in batch (for related steps at same level)
 * Uses the batch API to reduce API calls by ~60%
 * @param {string} planId - The plan ID
 * @param {Array<{stepId: string, stepData: Object}>} steps - Steps to expand
 * @param {Object} context - Context containing goal, formData, subcategory, planStructure
 * @returns {Promise<{success: boolean, results: Object[]}>}
 */
export const expandStepsBatch = async (planId, steps, context) => {
  // Filter to only steps that need expansion
  const stepsToExpand = steps.filter(({ stepData }) => needsExpansion(stepData));
  
  if (stepsToExpand.length === 0) {
    return { success: true, results: [] };
  }
  
  console.log(`Batch expanding ${stepsToExpand.length} steps...`);
  
  try {
    // Generate summary of previous steps for context
    const previousStepsSummary = generatePreviousStepsSummary(context.planStructure, stepsToExpand[0]?.stepId);
    
    // Use batch API for efficiency
    const result = await populateStepsBatch({
      planId,
      steps: stepsToExpand,
      goal: context.goal,
      formData: context.formData,
      subcategory: context.subcategory,
      previousStepsSummary,
    });
    
    if (result.processedSteps) {
      console.log(`Batch expansion complete: ${result.processedSteps.length} steps processed`);
      return {
        success: true,
        results: result.processedSteps,
        updatedPlanStructure: result.updatedPlanStructure,
      };
    }
    
    return { success: true, results: [] };
  } catch (error) {
    console.error('Batch expansion failed, falling back to sequential:', error);
    
    // Fallback to sequential processing if batch fails
    const results = [];
    let allSuccessful = true;
    
    for (const { stepId, stepData } of stepsToExpand) {
      const result = await expandStep(planId, stepId, stepData, context);
      
      results.push({
        stepId,
        ...result,
      });
      
      if (!result.success) {
        allSuccessful = false;
      }
      
      // Small delay between steps to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return {
      success: allSuccessful,
      results,
    };
  }
};

/**
 * Get loading status message
 * @param {string} phase - Current loading phase
 * @returns {string} User-friendly loading message
 */
export const getLoadingMessage = (phase) => {
  const messages = {
    'starting': 'Preparing to load step details...',
    'loading': 'Loading comprehensive content...',
    'processing': 'Generating personalized guidance...',
    'retrying': 'Retrying... Please wait.',
    'saving': 'Saving step details...',
    'complete': 'Step details loaded successfully!',
    'error': 'Failed to load step details.',
  };
  
  return messages[phase] || 'Loading...';
};

export default {
  expandStep,
  expandStepsBatch,
  needsExpansion,
  generatePreviousStepsSummary,
  getLoadingMessage,
};

